import { Request } from '@mam/main/ApiClient';
import {
	ErpPackResponse,
	ErpPackSessionPriceResponse,
	ErpPriceResponse,
	ErpSessionResponse,
	MoneyResponse,
	VirtualPackErpResponse,
	WrappedErpSessionDetailResponse,
} from 'com.cogvio.pricemonitor.api';

import type { ApiResult } from '@mam/main/ApiHooks';
import { DEFAULT_DECIMAL } from '~/UI/Input/DecimalInput';

export enum ERPSessionError {
	'erp-pack-not-found' = 'erp-pack-not-found',
	'internal-server-error' = 'internal-server-error',
}

export const getHumanReadableErrorCode = (
	codes: Set<string>,
	sessionId: string,
	erpPackId: string,
	url: string
): string | null => {
	return Array.from(codes)
		.filter((errorCode: string) => errorCode === ERPSessionError['erp-pack-not-found'])
		.reduce(
			() =>
				`You are seeing this because you are trying to reach the ${
					url || ''
				} URL. Either pack ${erpPackId} does not exist or webpage is not responding, because you do not have access rights to the session ${sessionId}. Either change pack ${erpPackId} or Session ${sessionId} in the web page address bar (URL bar) or return to the previous page.`,
			null
		);
};

// NOTE: this type is changed because com.cogvio.pricemonitor.api declares decimals as number but in reality it is a string
export type ERPSessionResponse = {
	erpSessionDetail: {
		erpSession: Omit<ErpSessionResponse, 'prices'> & {
			prices: (Omit<ErpPackSessionPriceResponse, 'price'> & {
				price: Omit<ErpPriceResponse, 'price'> & {
					price: Omit<MoneyResponse, 'value'> & {
						value: string;
					};
				};
			})[];
		};
		inputPrices: (Omit<ErpPackSessionPriceResponse, 'price'> & {
			price: Omit<ErpPriceResponse, 'price'> & {
				price: Omit<MoneyResponse, 'value'> & {
					value: string;
				};
			};
		})[];
		erpModelResult: VirtualPackErpResponse;
		erpPack: ErpPackResponse;
	};
};

export class ERPSessionRequest implements Request<ERPSessionResponse, WrappedErpSessionDetailResponse> {
	public readonly knownErrorCodes = Object.values(ERPSessionError);
	public readonly path: string;

	public constructor(userId: string, virtualPackId: string, sessionId: string) {
		this.path = `users/${userId}/erp/packages/${virtualPackId}/sessions/${sessionId}`;
	}

	decode(encodedResponse: WrappedErpSessionDetailResponse): ERPSessionResponse {
		return {
			...encodedResponse,
			erpSessionDetail: {
				...encodedResponse.erpSessionDetail,
				erpSession: {
					...encodedResponse.erpSessionDetail.erpSession,
					prices: encodedResponse.erpSessionDetail.erpSession.prices.map((inputPrice) => ({
						...inputPrice,
						price: {
							...inputPrice.price,
							price: {
								...inputPrice.price.price,
								value: Number(inputPrice.price.price.value).toFixed(DEFAULT_DECIMAL),
							},
						},
					})),
				},
				inputPrices: encodedResponse.erpSessionDetail.inputPrices.map((inputPrice) => ({
					...inputPrice,
					price: {
						...inputPrice.price,
						price: {
							...inputPrice.price.price,
							value: Number(inputPrice.price.price.value).toFixed(DEFAULT_DECIMAL),
						},
					},
				})),
			},
		};
	}
}

export type ERPRequestApiResult = ApiResult<ERPSessionResponse, ERPSessionError>;
